import type {
  BarState,
  ConstraintCtx,
  DragConstraint,
} from '../extensions/types.js';

export type ConstraintPipelineResult =
  | {
      readonly allowed: true;
      readonly state: BarState;
      readonly warnings: readonly string[];
    }
  | {
      readonly allowed: false;
      readonly warning: string;
    };

/**
 * Runs a proposed bar state through every constraint in priority order.
 * Adjustments accumulate — each constraint sees the running state from
 * the previous one, not the original proposal. A single `allowed: false`
 * short-circuits the chain and surfaces its warning to the caller so
 * the drag tooltip can display it.
 */
export function runConstraintPipeline(
  proposed: BarState,
  constraints: readonly DragConstraint[],
  ctx: ConstraintCtx
): ConstraintPipelineResult {
  let state = proposed;
  const warnings: string[] = [];
  for (const constraint of constraints) {
    const result = constraint.validate(state, ctx);
    if (!result.allowed) {
      return { allowed: false, warning: result.warning };
    }
    if (result.warning) warnings.push(result.warning);
    if (result.adjustedState) state = result.adjustedState;
  }
  return { allowed: true, state, warnings };
}
